import React, { useMemo, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { LibraryIcon, SearchIcon, AlertTriangleIcon, HeartIcon, TagIcon } from 'lucide-react';
import { AfterTideLayout } from '../../components/AfterTide/AfterTideLayout';
import { aftertideBooks } from '../../data/aftertideBooksData';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
export default function ContentBrowse() {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [activeTab, setActiveTab] = useState<'tropes' | 'relationships' | 'warnings'>('tropes');
  const [searchQuery, setSearchQuery] = useState('');
  // Extract Tropes
  const tropesData = useMemo(() => {
    const counts: Record<string, number> = {};
    aftertideBooks.forEach(b => {
      b.tropes.forEach(t => {
        counts[t] = (counts[t] || 0) + 1;
      });
    });
    return Object.entries(counts).map(([name, count]) => ({
      name,
      count
    })).sort((a, b) => b.count - a.count);
  }, []);
  // Extract Relationship Types
  const relationshipData = useMemo(() => {
    const counts: Record<string, number> = {};
    aftertideBooks.forEach(b => {
      counts[b.relationshipType] = (counts[b.relationshipType] || 0) + 1;
    });
    return Object.entries(counts).map(([name, count]) => ({
      name,
      count
    })).sort((a, b) => b.count - a.count);
  }, []);
  // Extract Warnings
  const warningsData = useMemo(() => {
    const grouped: Record<string, {
      name: string;
      count: number;
    }[]> = {};
    aftertideBooks.forEach(b => {
      b.contentWarnings.forEach(cw => {
        if (!grouped[cw.category]) grouped[cw.category] = [];
        cw.items.forEach(item => {
          const existing = grouped[cw.category].find(i => i.name === item);
          if (existing) {
            existing.count++;
          } else {
            grouped[cw.category].push({
              name: item,
              count: 1
            });
          }
        });
      });
    });
    // Sort items within categories
    Object.keys(grouped).forEach(cat => {
      grouped[cat].sort((a, b) => b.count - a.count);
    });
    return grouped;
  }, []);
  const filteredTropes = tropesData.filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()));
  return <AfterTideLayout data-id="element-5065" data-mp="118enxu0ftuz9">
      <div className={`min-h-screen ${dk.page} py-12`} data-id="element-5066" data-mp="119pug1l9pgu2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5067" data-mp="w8k05zbyllif">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10" data-id="element-5068" data-mp="wb63fmrtheuu">
            <div data-id="element-5069" data-mp="s9kn1rg2qifw">
              <div className="flex items-center gap-3 mb-2" data-id="element-5070" data-mp="n8dfnthufl9r">
                <LibraryIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-5071" data-mp="n9ybrnjt3c9q" />
                <h1 className={`font-playfair text-4xl font-bold ${dk.textPrimary}`} data-id="element-5072" data-mp="2mkg65novxfwx">
                  Explore Content
                </h1>
              </div>
              <p className={`${dk.textSecondary}`} data-id="element-5073" data-mp="2p6p8p3spw7tt">
                Browse our catalog by specific themes, dynamics, and content
                warnings.
              </p>
            </div>

            <div className="relative w-full md:w-72" data-id="element-5074" data-mp="2yv77xglcm6vc">
              <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.textSecondary}`} data-id="element-5075" data-mp="2yxtmi06npmn2" />
              <input type="text" placeholder={`Search ${activeTab}...`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full ${dk.surface} border ${dk.borderAccentStrong} ${dk.textPrimary} rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:border-[#e2b04a]`} data-id="element-5076" data-mp="267l01w538g2o" />
            </div>
          </div>

          {/* Tabs */}
          <div className={`flex border-b ${dk.border} mb-8`} data-id="element-5077" data-mp="3rk4onf9wvvlz">
            <button onClick={() => setActiveTab('tropes')} className={`flex items-center px-6 py-4 font-medium text-sm transition-colors border-b-2 ${activeTab === 'tropes' ? `border-[#e2b04a] ${rt.textPrimary}` : `border-transparent ${dk.textSecondary} hover:text-[#f5f0e8]`}`} data-id="element-5078" data-mp="3vl7zfnd3r1gc">
              <TagIcon className="h-4 w-4 mr-2" data-id="element-5079" data-mp="7z73m22oy6no" />
              Tropes & Themes
            </button>
            <button onClick={() => setActiveTab('relationships')} className={`flex items-center px-6 py-4 font-medium text-sm transition-colors border-b-2 ${activeTab === 'relationships' ? `border-[#e2b04a] ${rt.textPrimary}` : `border-transparent ${dk.textSecondary} hover:text-[#f5f0e8]`}`} data-id="element-5080" data-mp="2wy8az2cisie">
              <HeartIcon className="h-4 w-4 mr-2" data-id="element-5081" data-mp="qpycl2irnv1j" />
              Relationship Dynamics
            </button>
            <button onClick={() => setActiveTab('warnings')} className={`flex items-center px-6 py-4 font-medium text-sm transition-colors border-b-2 ${activeTab === 'warnings' ? `border-[#e2b04a] ${rt.textPrimary}` : `border-transparent ${dk.textSecondary} hover:text-[#f5f0e8]`}`} data-id="element-5082" data-mp="zos4ifl5jx8r">
              <AlertTriangleIcon className="h-4 w-4 mr-2" data-id="element-5083" data-mp="1jatz1pgfinst" />
              Content Warnings
            </button>
          </div>

          {/* Tab Content */}
          <div className="min-h-[400px]" data-id="element-5084" data-mp="15d5ao7n5kyj0">
            {activeTab === 'tropes' && <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-id="element-5085" data-mp="1ac04s5qkm9if">
                {filteredTropes.map(trope => <Link key={trope.name} to={`/aftertide/browse?trope=${encodeURIComponent(trope.name)}`} className={`${dk.surface} border ${dk.border} hover:border-[#e2b04a]/50 rounded-xl p-4 transition-colors group`} data-id="element-5086" data-mp="10bzh67lo578f">
                    <h3 className={`font-bold ${dk.textPrimary} group-hover:text-[#e2b04a] transition-colors mb-1`} data-id="element-5087" data-mp="clvfw6vydjkl">
                      {trope.name}
                    </h3>
                    <p className={`text-xs ${dk.textSecondary}`} data-id="element-5088" data-mp="2jb752k84gk63">
                      {trope.count} {trope.count === 1 ? 'book' : 'books'}
                    </p>
                  </Link>)}
              </div>}

            {activeTab === 'relationships' && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5089" data-mp="25k2155xq15py">
                {relationshipData.map(rel => <Link key={rel.name} to={`/aftertide/browse?relationship=${encodeURIComponent(rel.name)}`} className={`${dk.surface} border ${dk.border} hover:border-[#c77dba]/50 rounded-xl p-6 transition-colors group flex items-center justify-between`} data-id="element-5090" data-mp="3275lw4vqmvxt">
                    <div data-id="element-5091" data-mp="1htxzh443fg1l">
                      <h3 className={`font-playfair text-2xl font-bold ${dk.textPrimary} group-hover:text-[#c77dba] transition-colors mb-1`} data-id="element-5092" data-mp="1hu7q8twypr8c">
                        {rel.name}
                      </h3>
                      <p className={`text-sm ${dk.textSecondary}`} data-id="element-5093" data-mp="17phkse2co4zd">
                        {rel.count} {rel.count === 1 ? 'book' : 'books'}
                      </p>
                    </div>
                    <HeartIcon className={`h-8 w-8 ${dk.textMuted} group-hover:text-[#c77dba] transition-colors`} data-id="element-5094" data-mp="1cmq26cfoc4zk" />
                  </Link>)}
              </div>}

            {activeTab === 'warnings' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-5095" data-mp="1qe4p2ucpcqbw">
                {Object.entries(warningsData).map(([category, items]) => <div key={category} className={`${dk.surface} border ${dk.border} rounded-xl p-6`} data-id="element-5096" data-mp="20eez663fxhmu">
                    <h3 className={`font-playfair text-xl font-bold ${rt.textPrimary} mb-4 border-b ${dk.borderAccentStrong} pb-2`} data-id="element-5097" data-mp="604co4abizk8">
                      {category}
                    </h3>
                    <ul className="space-y-3" data-id="element-5098" data-mp="22zd1fwd1djxd">
                      {items.filter(i => i.name.toLowerCase().includes(searchQuery.toLowerCase())).map(item => <li key={item.name} className="flex justify-between items-center" data-id="element-5099" data-mp="1ozbzedl1c6gb">
                            <span className={`text-sm ${dk.textPrimary}`} data-id="element-5100" data-mp="1y0jllxaa1gu1">
                              {item.name}
                            </span>
                            <span className={`text-xs ${dk.surface} border ${dk.border} px-2 py-1 rounded-sm text-[#a0a0b0]`} data-id="element-5101" data-mp="1u09h6p24uyha">
                              {item.count}
                            </span>
                          </li>)}
                    </ul>
                  </div>)}
              </div>}
          </div>
        </div>
      </div>
    </AfterTideLayout>;
}